// ---------- le jeu à l'IA : les règles que le jeu ET le Worker lisent ----------
//
// Depuis le 2026-11-04, une ligne ne se devine plus à partir de quatre mots fixes : elle se joue
// contre une IA (Workers AI, côté Worker — jamais Claude pendant une partie, cf. backend/bench/).
// Deux modes, un par partie :
//
//   - « classic » : l'IA connaît le mot, en donne un indice, et RELANCE d'après les propositions
//     fausses du joueur. Le premier indice est figé (le premier des quatre mots de l'énigme), pour
//     que tout le monde commence la même partie et que l'ouverture ne coûte aucun appel ;
//   - « inverse » : le joueur voit le mot et le fait deviner à l'IA, un mot-indice par coup. L'IA
//     est AVEUGLE : ni le mot, ni sa longueur ne lui sont envoyés ; c'est le Worker qui compare.
//
// L'ÉTAT D'UNE PARTIE VIT SUR LE SERVEUR (table `ai_plays`), pas sur l'appareil : chaque coup
// coûte un appel d'IA, et un compteur que le client tiendrait se contournerait en vidant le
// localStorage. Le jeu n'en garde que le résultat (les étoiles, src/store.ts).

import { gridWord } from './normalize';

export type PlayMode = 'classic' | 'inverse';
export const PLAY_MODES: readonly PlayMode[] = ['classic', 'inverse'];

/** Le nombre de coups d'une ligne avant qu'il faille un bonus, par défaut (réglable par partie
 *  dans l'atelier). */
export const DEFAULT_MAX_MOVES = 8;

/** Le porte-monnaie de bonus. Réglages du prototype, réunis ici pour se retrouver d'un coup. */
export const BONUS = {
  /** le stock d'un compte neuf */
  start: 10,
  /** ce qu'un bonus rouvre de coups sur une ligne bloquée */
  moves: 3,
  /** ce que coûte de révéler (ou passer) une ligne */
  reveal: 2,
  /** ce que rapporte une pub regardée */
  ad: 3,
  /** trois étoiles */
  perfect: 2,
  /** le mot mystère trouvé avant la dernière ligne */
  mystery: 1,
  /** la dernière partie d'un chapitre gagnée : le chapitre est fini */
  chapter: 3,
  /** le mot du jour gagné le jour même */
  daily: 1,
} as const;

/** Une ligne en cours, telle que le Worker la garde et la renvoie. */
export interface AiRow {
  /** classique : les indices de l'IA ; inversé : ceux du joueur */
  clues: string[];
  /** classique : les propositions du joueur ; inversé : celles de l'IA */
  guesses: string[];
  moves: number;
  /** coups permis : le maximum de la partie, plus ce que les bonus ont rouvert */
  allow: number;
  /** 0 en cours, 1 trouvée, 2 révélée (passée) */
  done: 0 | 1 | 2;
}

export interface AiState {
  rows: AiRow[];
  /** le mot mystère est trouvé (par le joueur en classique, par l'IA en inversé) */
  mystery: boolean;
  /** les étoiles, posées quand toutes les lignes sont finies */
  stars?: number;
  /** la partie a déjà rapporté ses gains, une fois pour toutes : survit à « Rejouer », sans quoi
   *  rejouer une partie facile serait une source de bonus sans fin */
  paid?: true;
}

/** La réponse de chaque route `/ai` : l'état entier (le jeu ne recompose rien) et le stock. */
export interface AiReply {
  state: AiState;
  bonus: number;
  /** un coup refusé SANS être compté (indice interdit, déjà proposé…), à dire au joueur. Pas la
   *  longueur : une proposition trop longue ou trop courte se joue, et compte. */
  refused?: 'family' | 'repeat' | 'locked' | 'empty' | 'funds';
  /** inversé : l'IA vient de tenter le mot mystère */
  mysteryTry?: string;
  /** les bonus gagnés par CE coup (trois étoiles, mot mystère trouvé tôt) */
  earned?: number;
}

export type AiAction =
  | { kind: 'play'; row: number; text: string }
  | { kind: 'mystery'; row: number; text: string }
  | { kind: 'bonus'; row: number }
  | { kind: 'reveal'; row: number }
  | { kind: 'restart' };

export const freshState = (
  rows: number,
  maxMoves: number,
  firstClues: (string | null)[],
): AiState => ({
  rows: Array.from({ length: rows }, (_, i) => ({
    clues: firstClues[i] ? [firstClues[i]!] : [],
    guesses: [],
    moves: 0,
    allow: maxMoves,
    done: 0,
  })),
  mystery: false,
});

/**
 * La règle de famille : le mot lui-même, l'un qui contient l'autre (4 lettres au moins), ou un
 * préfixe commun de 5 lettres. Elle attrape chaton et chatte pour CHAT, et laisse passer
 * « pluvieux » pour PLUIE — c'est accepté, cf. `forbiddenClue`.
 */
export function sameFamily(a: string, b: string): boolean {
  const A = gridWord(a);
  const B = gridWord(b);
  if (!A || !B) return false;
  if (A === B) return true;
  const [s, l] = A.length <= B.length ? [A, B] : [B, A];
  if (s.length >= 4 && l.includes(s)) return true;
  let p = 0;
  while (p < s.length && s[p] === l[p]) p++;
  return p >= 5;
}

/**
 * Ce qu'un joueur n'a PAS le droit de donner comme indice en mode inversé : le mot, sa famille
 * (`sameFamily`), ou ses lettres mélangées ou à l'envers (« tahc » pour CHAT). Rien d'autre.
 *
 * PAS D'ARBITRE IA (retiré le 2026-11-04). Le banc `backend/bench/judge.ts` l'a montré : aucun
 * modèle bon marché ne refuse les traductions sans refuser aussi des indices honnêtes — qwen3
 * refusait sombrero, béret, camembert pour leurs réponses, et « TETE » pour CHAPEAU parce que le
 * clavier du jeu n'a pas d'accents. Une consigne plus permissive laissait passer 14 tricheries
 * sur 17. D'où le choix, après les refus que Vincent a rencontrés en jouant : un refus injuste
 * bloque un joueur honnête, alors qu'une traduction (« hat ») ne rend que SA ligne plus facile.
 * Les traductions passent donc.
 */
export function forbiddenClue(clue: string, answer: string): boolean {
  if (sameFamily(clue, answer)) return true;
  const C = gridWord(clue);
  const A = gridWord(answer);
  return (
    C.length >= 3 && C.length === A.length && [...C].sort().join('') === [...A].sort().join('')
  );
}

/**
 * Les étoiles d'une partie finie, d'après le total des coups. Une ligne révélée compte le maximum
 * de coups plus deux : passer ne doit jamais valoir mieux que chercher. Seuils par ligne : 3 coups
 * en moyenne pour trois étoiles, 5 pour deux — le banc d'essai (backend/bench/) trouve un mot en
 * 2 à 3 coups avec un joueur appliqué.
 */
export function aiStars(s: AiState, maxMoves: number): number {
  const n = s.rows.length;
  const total = s.rows.reduce((a, r) => a + (r.done === 2 ? maxMoves + 2 : r.moves), 0);
  return total <= n * 3 ? 3 : total <= n * 5 ? 2 : 1;
}
